import { Pipe, PipeTransform } from '@angular/core';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

type ShowTimeZone = 'short' | 'long' | boolean;
type DisplayMode = 'customer' | 'user';

@Pipe({
  name: 'appointmentTime',
  pure: true,
})
export class AppointmentTimePipe implements PipeTransform {
  transform(
    appointment: any,
    which: 'start' | 'end',
    formatStr: 'shortTime' | 'fullDate' | 'shortDate' | string,
    showTimeZone?: ShowTimeZone,
    mode: DisplayMode = 'customer'
  ): string | null {
    // console.log('AppointmentTimePipe transform called with:', {
    //   appointment,
    //   which,
    //   formatStr,
    //   showTimeZone,
    //   mode,
    // });

    if (!appointment) return null;

    const timeZoneId = appointment.timeZoneId;
    if (!timeZoneId) return null;

    // --- Determine base UTC date ---
    const utcIso =
      which === 'start' ? appointment.startTimeUTC : appointment.endTimeUTC;

    let utcDate: Date | null = null;

    if (this.isValidUtc(utcIso)) {
      utcDate = new Date(utcIso);
    } else {
      // Legacy fallback
      const time =
        which === 'start' ? appointment.startTime : appointment.endTime;

      const date = appointment.scheduledDate ?? appointment.date;

      if (!date || !time) return null;

      utcDate = zonedTimeToUtc(this.combine(date, time), timeZoneId);
    }

    // --- Convert for display ---
    const displayDate =
      mode === 'customer' ? utcToZonedTime(utcDate, timeZoneId) : utcDate; // browser local time

    const formatted = this.formatDate(displayDate, formatStr);

    const tzSuffix =
      mode === 'customer'
        ? this.getTimeZoneSuffix(displayDate, timeZoneId, showTimeZone)
        : null;

    return tzSuffix ? `${formatted} ${tzSuffix}` : formatted;
  }

  // ---- helpers ----

  private isValidUtc(value?: string): boolean {
    if (!value) return false;
    const d = new Date(value);
    if (isNaN(d.getTime())) return false;
    if (d.getFullYear() < 1971) return false; // 👈 critical
    return true;
  }

  private combine(date: string | Date, time: string): Date {
    let base: Date;

    if (date instanceof Date) {
      base = new Date(date);
    } else {
      const s = String(date);

      // If it's an ISO day string like "YYYY-MM-DD", force local midnight to avoid UTC parsing drift
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        base = new Date(`${s.slice(0, 10)}T00:00:00`);
      } else {
        base = new Date(s);
      }
    }

    const [hours, minutes] = time.split(':').slice(0, 2).map(Number);
    base.setHours(hours, minutes, 0, 0);

    return base;
  }

  private formatDate(date: Date, formatStr: string): string {
    switch (formatStr) {
      case 'shortTime':
        return format(date, 'h:mm a');

      case 'fullDate':
        return format(date, 'EEEE, MMMM d, yyyy');

      case 'shortDate':
        return format(date, 'M/d/yyyy');

      default:
        return format(date, formatStr);
    }
  }

  private getTimeZoneSuffix(
    date: Date,
    timeZoneId: string,
    showTimeZone?: ShowTimeZone
  ): string | null {
    if (!showTimeZone) return null;

    const style = showTimeZone === 'long' ? 'long' : 'short';

    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone: timeZoneId,
        timeZoneName: style,
      })
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName')?.value ?? null
    );
  }
}
